/* eslint-disable no-bitwise */
import * as t from 'io-ts';
import {isBoolean, isString} from 'lodash';

import {REmailFormats} from '../../helpers/models/emailFormatHelpers';
import {RCellsContext} from '../../store/panels/cells/context/cellsContext';
import {
  makeApiType,
  makeEnumRuntimeType,
  RCalendarEventUidRef,
  RChannelIdRef,
  RConversationIdRef,
  RLazyTeammateIdRef,
  RMessageUidRef,
  RTagIdRef,
} from './apiTypes';
import {parseEmlAttachment, parseUploadedAttachment, RAttachment} from './attachmentModel';
import {
  parseMessageError,
  parseSecurityError,
  RMessageError,
  RMessageSecurityError,
} from './messageErrorModel';
import {parseRecipient, RRecipient} from './recipientModel';

/*
 * Runtime type.
 */

export enum MessageSourcesEnum {
  GMAIL = 'gmail',
}
export const RMessageSourceType = makeEnumRuntimeType(MessageSourcesEnum);

export enum MessageTypesEnum {
  INTERNAL = 'internal',
  EMAIL = 'email',
  TWEET = 'tweet',
  TWEET_DM = 'tweet_dm',
  CALL = 'call',
  SMS = 'sms',
  GOOGLE_PLAY = 'google_play',
  YALO_WHATSAPP = 'yalo_wha',
  WHATSAPP = 'whatsapp',
  FACEBOOK = 'facebook',
  INTERCOM = 'intercom',
  SMOOCH = 'smooch',
  LAYER = 'layer',
  CUSTOM = 'custom',
  FRONT_CHAT = 'front_chat',
}
export const RMessageType = makeEnumRuntimeType(MessageTypesEnum);

export const replyOnlyMessageTypes = [
  MessageTypesEnum.INTERCOM,
  MessageTypesEnum.FACEBOOK,
  MessageTypesEnum.CALL,
  MessageTypesEnum.SMOOCH,
  MessageTypesEnum.LAYER,
  MessageTypesEnum.CUSTOM,
  MessageTypesEnum.GOOGLE_PLAY,
  MessageTypesEnum.FRONT_CHAT,
];

export const replyOnSameChannelMessageTypes = [
  MessageTypesEnum.INTERCOM,
  MessageTypesEnum.FACEBOOK,
  MessageTypesEnum.SMOOCH,
  MessageTypesEnum.LAYER,
  MessageTypesEnum.CUSTOM,
  MessageTypesEnum.GOOGLE_PLAY,
  MessageTypesEnum.TWEET_DM,
  MessageTypesEnum.FRONT_CHAT,
];

export enum MessageVisualTypesEnum {
  EMAIL = 'EMAIL',
  TWEET = 'TWEET',
  CHAT = 'CHAT',
}

export enum MessageStatusesEnum {
  SENT = 'sent',
  IMPORTED = 'imported',
  DRAFT_COMPOSE = 'draft-compose',
  /** Draft has been queued offline for sending. */
  DRAFT_PENDING = 'draft-pending',
  /** Draft is being sent and can be canceled. */
  DRAFT_SENDING = 'draft-sending',
  DRAFT_LATER = 'draft-later',
  DRAFT_FAILED = 'draft-failed',
}

export enum MessageVisualStatusesEnum {
  PARTIAL = 'PARTIAL',
  TIMELINE = 'TIMELINE',
  DRAFT = 'DRAFT',
  EML_FILE = 'EML_FILE',
}

export enum MessageSortPropertiesEnum {
  CREATED_AT = 'created_at',
  DATE = 'date',
}

export enum SendActionsEnum {
  SEND = 'send',
  SEND_AND_ARCHIVE = 'sendAndArchive',
}

export const RMessageSortProperty = makeEnumRuntimeType(MessageSortPropertiesEnum);

const RSendMessageOptions = t.intersection([
  t.type({
    sendAction: makeEnumRuntimeType(SendActionsEnum),
    cellsContext: RCellsContext,
  }),
  t.partial({
    sendLaterAt: t.Integer,
    remindAt: t.Integer,
    tagIds: t.readonlyArray(RTagIdRef),
  }),
]);

const RTwitterExtra = t.type({
  tweetUrl: t.string,
  isLiked: t.boolean,
  isRetweeted: t.boolean,
});

const RTwitterDmExtra = t.type({
  dmUrl: t.string,
});

const RIntercomExtra = t.type({
  intercomUrl: t.string,
});

const RLayerExtra = t.type({
  layerPageUrl: t.string,
});

// The last message should not reference the conversation to avoid a circular reference.
const RLastMessageConversationRef = t.type({
  isLastMessage: t.literal(true),
  isLastManualMessage: t.boolean,
  conversationId: t.undefined,
});

// The last manual message should not reference the conversation to avoid a circular reference.
const RLastManualMessageConversationRef = t.type({
  isLastManualMessage: t.literal(true),
  isLastMessage: t.boolean,
  conversationId: t.undefined,
});

const RNonLastMessageConversationRef = t.type({
  isLastMessage: t.literal(false),
  isLastManualMessage: t.literal(false),
  conversationId: RConversationIdRef,
});

// TODO: Remove this when the garbage collector can handle cycles and we can just use message.conversationId.
const RMessageConversationRef = t.union([
  RLastMessageConversationRef,
  RLastManualMessageConversationRef,
  RNonLastMessageConversationRef,
]);

// Common properties between all messages.
const RMessageBase = t.intersection([
  t.type({
    id: t.Integer,
    uid: t.string,
    url: t.string,
    conversationId: t.number,
    conversationRef: RMessageConversationRef,
    conversationUrl: t.string,
    type: RMessageType,
    visualType: makeEnumRuntimeType(MessageVisualTypesEnum),
    status: makeEnumRuntimeType(MessageStatusesEnum),
    blurb: t.string,
    isSeen: t.boolean,
    isSenderUnverified: t.boolean,
    isReply: t.boolean,
    isInbound: t.boolean,
    hasMissingBody: t.boolean,
    recipients: t.readonlyArray(RRecipient),
    attachments: t.readonlyArray(RAttachment),
    calendarEventUids: t.readonlyArray(RCalendarEventUidRef),
    sentAt: t.Integer,
    createdAt: t.Integer,
    subject: t.string,
  }),
  t.partial({
    updatedAt: t.Integer,
    authorId: RLazyTeammateIdRef,
    authorIds: t.readonlyArray(t.Integer),
    addAuthorIds: t.readonlyArray(t.Integer),
    extId: t.string,
    source: RMessageSourceType,
    securityError: RMessageSecurityError,
  }),
]);

const RPartialMessage = t.intersection([
  RMessageBase,
  t.type({
    visualStatus: t.literal(MessageVisualStatusesEnum.PARTIAL),
  }),
]);

const RLoadedMessageBase = t.intersection([
  RMessageBase,
  t.type({
    messageUrl: t.string,
    body: t.string,
    text: t.string,
    updatedAt: t.Integer,
    hasQuote: t.boolean,
  }),
  t.partial({
    lightBody: t.string,
    wasQuoteModified: t.boolean,
    cannedAnswerId: t.Integer,
  }),
]);

const RTimelineMessage = t.intersection([
  RLoadedMessageBase,
  t.type({
    visualStatus: t.literal(MessageVisualStatusesEnum.TIMELINE),
  }),
  t.partial({
    twitterExtra: RTwitterExtra,
    twitterDmExtra: RTwitterDmExtra,
    intercomExtra: RIntercomExtra,
    layerExtra: RLayerExtra,
  }),
]);

const RDraftMessage = t.intersection([
  RLoadedMessageBase,
  t.type({
    visualStatus: t.literal(MessageVisualStatusesEnum.DRAFT),
    fromChannelId: RChannelIdRef,
    lastNonVisitorAuthorId: RLazyTeammateIdRef,
    /** NOTE: with the `hasAutoShareDrafts` FF this property is only used as a fallback. Look for `isMessageVisuallyShared` for details. */
    isShared: t.boolean,
    /** Indicates whether this draft has changes that have not been saved to the server. */
    isSaving: t.boolean,
    /** Indicates whether the server knows about this draft, i.e. whether it has ever been saved. */
    wasSaved: t.boolean,

    text: t.string,
    html: t.string,
  }),
  t.partial({
    quoteHtml: t.string,
    isQuoteIncluded: t.boolean,

    forwardHtml: t.string,
    isForwardIncluded: t.boolean,
    wasForwardModified: t.boolean,

    signatureId: t.Integer,
    signatureHtml: t.string,
    isSignatureIncluded: t.boolean,
    wasSignatureModified: t.boolean,

    format: t.union([REmailFormats, t.undefined]),
    defaultFontStyle: t.string,
    inReplyToId: t.Integer,

    error: RMessageError,
    sendOptions: RSendMessageOptions,
    /** ID of the offline task that is scheduled to send the message. */
    sendOfflineTaskId: t.string,
    /** Server version of this draft, if it has ever been saved. This draft may have local changes, though. */
    version: t.string,
    /** Attachments from a forwarded EML file message. */
    emlAttachments: t.readonlyArray(RAttachment),
  }),
]);

const REmlMessage = t.intersection([
  RLoadedMessageBase,
  t.type({
    visualStatus: t.literal(MessageVisualStatusesEnum.EML_FILE),
  }),
]);

export const RLoadedMessage = t.taggedUnion('visualStatus', [RTimelineMessage, RDraftMessage, REmlMessage]);

export const RMessage = t.taggedUnion(
  'visualStatus',
  [RPartialMessage, RTimelineMessage, RDraftMessage, REmlMessage],
  'message',
);
RMessageUidRef.setTargetType(RMessage);

/*
 * Types.
 */

export interface SendMessageOptionsModel extends Readonly<t.TypeOf<typeof RSendMessageOptions>> {}
export interface TwitterExtra extends Readonly<t.TypeOf<typeof RTwitterExtra>> {}
export interface TwitterDmExtra extends Readonly<t.TypeOf<typeof RTwitterDmExtra>> {}

export interface PartialMessageModel extends Readonly<t.TypeOf<typeof RPartialMessage>> {}
export interface TimelineMessageModel extends Readonly<t.TypeOf<typeof RTimelineMessage>> {}
export interface DraftMessageModel extends Readonly<t.TypeOf<typeof RDraftMessage>> {}
export interface EmlMessage extends Readonly<t.TypeOf<typeof REmlMessage>> {}

export type LoadedMessageModel = TimelineMessageModel | DraftMessageModel | EmlMessage;
export type MessageModel = PartialMessageModel | TimelineMessageModel | DraftMessageModel | EmlMessage;

/*
 * IO methods.
 */

export function parseMessage(src: any): MessageModel {
  const baseMessage = {
    id: src.id,
    uid: src.uid,
    url: src.in_conversation_url,
    messageUrl: src.url,
    conversationId: src.conversation_id,
    conversationRef: {
      isLastMessage: false,
      isLastManualMessage: false,
      conversationId: src.conversation_id,
    },
    conversationUrl: src.conversation_url,
    authorId: src.author_id || undefined,
    authorIds: src.author_ids || undefined,
    extId: src.ext_id,
    type: parseMessageType(src.type),
    visualType: visualTypeFromType(src.type),
    status: parseMessageStatus(src),
    source: parseMessageSource(src.source),
    blurb: src.blurb,
    isSeen: Boolean(src.recipient_opened_date),
    isSenderUnverified: Boolean(src.from && src.from.sender_unverified),
    isReply: src.is_reply || false,
    isInbound: src.inbound || false,
    recipients: src.recipients && src.recipients.map(parseRecipient),
    attachments: src.attachments && src.attachments.map(parseUploadedAttachment),
    calendarEventUids: (src.calendar_event_versions || []).map((c: any) => c.calendar_event.uid),
    securityError: (src.security_error && parseSecurityError(src)) || undefined,
    hasMissingBody: Boolean(src.has_missing_body),
    sentAt: src.date,
    createdAt: src.created_at,
    updatedAt: src.updated_at || src.date || undefined,
    wasQuoteModified: src.quote_was_modified || false,
    subject: src.subject || '',
    cannedAnswerId: src.canned_answer_id,
  };

  // If the message is not fully loaded, return the partial version.
  if (!src.content_loaded || src.render_full === false) {
    return {
      ...baseMessage,
      visualStatus: MessageVisualStatusesEnum.PARTIAL,
    };
  }

  const baseLoadedMessage = {
    ...baseMessage,
    body: src.body || '',
    text: src.text || '',
    lightBody: src.light_body,
    hasQuote: Boolean(src.has_quote),
  };

  // If this is not a draft, return as a timeline message.
  const visualStatus = visualStatusFromType(src.status);
  if (visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    return {
      ...baseLoadedMessage,
      visualStatus: MessageVisualStatusesEnum.TIMELINE,
      twitterExtra: parseTwitterExtra(src),
      twitterDmExtra: parseTwitterDmExtra(src),
      intercomExtra: parseIntercomExtra(src),
      layerExtra: parseLayerExtra(src),
    };
  }

  // Otherwise, parse as a draft.
  return {
    ...baseLoadedMessage,
    visualStatus: MessageVisualStatusesEnum.DRAFT,
    fromChannelId: src.from && src.from.channel_id,
    lastNonVisitorAuthorId: src.last_non_visitor_author_id,
    isShared: src.shared_draft || false,
    text: src.text || '',
    html: src.html || '',
    isSaving: false,
    wasSaved: true,

    quoteHtml: isString(src.quote_html) ? src.quote_html : undefined,
    isQuoteIncluded: src.quote_include || undefined,
    wasQuoteModified: src.quote_modified || undefined,

    forwardHtml: isString(src.forward_html) ? src.forward_html : undefined,
    isForwardIncluded: src.forward_include || undefined,
    wasForwardModified: src.forward_modified || undefined,

    signatureId: (src.signature && src.signature.id) || undefined,
    signatureHtml: src.signature_html ? src.signature_html : undefined,
    isSignatureIncluded: src.signature_include || undefined,
    wasSignatureModified: src.signature_modified || undefined,

    format: src.format || undefined,
    defaultFontStyle: src.default_font_style || '',
    inReplyToId: src.in_reply_to_id || undefined,
    isReply: Boolean(src.in_reply_to_id) || src.is_reply || false,

    error: (src.error_type && parseMessageError(src)) || undefined,
    sendOptions: undefined,
    version: src.version || (src.updated_at || src.date).toString(),
  };
}

function parseMessageStatus(messageSrc: any): MessageStatusesEnum {
  // If error while saving message, update status to DRAFT_FAILED.
  if (messageSrc.error_type && messageSrc.status === MessageStatusesEnum.DRAFT_COMPOSE) {
    return MessageStatusesEnum.DRAFT_FAILED;
  }

  return messageSrc.status;
}

function parseMessageSource(src: any) {
  if (RMessageSourceType.is(src)) {
    return src;
  }

  return undefined;
}

export enum ConversationSrcMessageKeysEnum {
  LAST_MESSAGE = 'last_message',
  LAST_MANUAL_MESSAGE = 'last_manual_message',
}

type PartialLastMessageConversationRef =
  | {isLastMessage: true; isLastManualMessage: boolean}
  | {isLastMessage: boolean; isLastManualMessage: true};

export function parseMessageIncludedInConversation(
  conversationSrc: any,
  messageKey: ConversationSrcMessageKeysEnum,
  conversationRef: PartialLastMessageConversationRef,
): MessageModel {
  const message = conversationSrc[messageKey];
  const parsedMessage = parseMessage({
    ...message,
    in_conversation_url: `${conversationSrc.url}/messages/${message.uid}`,
    url: '',
    conversation_id: conversationSrc.id,
    conversation_url: conversationSrc.url,
    type: conversationSrc.message_type,
    recipients: [],
    attachments: [],
    is_reply: isBoolean(message.is_reply) ? message.is_reply : Boolean(conversationSrc.replied),
    subject: conversationSrc.subject,
  });

  return {
    ...parsedMessage,
    conversationRef: {
      ...conversationRef,
      conversationId: undefined,
    },
  };
}

export function parseMessageType(src: any) {
  switch (src) {
    case 'phone-call':
    case 'truly-call':
      return MessageTypesEnum.CALL;

    case 'googleplay':
      return MessageTypesEnum.GOOGLE_PLAY;

    case 'layer':
      return MessageTypesEnum.FRONT_CHAT;

    default:
      return src;
  }
}

export function visualTypeFromType(type: MessageTypesEnum) {
  switch (type) {
    case MessageTypesEnum.EMAIL:
      return MessageVisualTypesEnum.EMAIL;

    case MessageTypesEnum.TWEET:
      return MessageVisualTypesEnum.TWEET;

    default:
      return MessageVisualTypesEnum.CHAT;
  }
}

function visualStatusFromType(status: MessageStatusesEnum) {
  switch (status) {
    case MessageStatusesEnum.SENT:
    case MessageStatusesEnum.IMPORTED:
      return MessageVisualStatusesEnum.TIMELINE;

    default:
      return MessageVisualStatusesEnum.DRAFT;
  }
}

// Extensions only available on tweets.
function parseTwitterExtra(src: any): TwitterExtra | undefined {
  if (src.type !== 'tweet' || !src.tweet_url) {
    return undefined;
  }

  return {
    tweetUrl: src.tweet_url,
    isLiked: Boolean(src.tweet_favorited),
    isRetweeted: Boolean(src.retweeted),
  };
}

// Extensions only available on Twitter DMs.
function parseTwitterDmExtra(src: any): TwitterDmExtra | undefined {
  if (src.type !== 'tweet_dm' || !src.dm_url) {
    return undefined;
  }

  return {
    dmUrl: src.dm_url,
  };
}

// Extensions only available on Intercom messages.
function parseIntercomExtra(src: any) {
  if (src.type !== 'intercom' || !src.intercom_url) {
    return undefined;
  }

  return {intercomUrl: src.intercom_url};
}

function parseLayerExtra(src: any) {
  if (
    !['front_chat', 'layer'].includes(src.type) ||
    !src.headers ||
    (!src.headers.layer_page_url && !src.headers.front_chat_page_url)
  ) {
    return undefined;
  }

  return {layerPageUrl: src.headers.layer_page_url || src.headers.front_chat_page_url};
}

export function buildEmlMessageUid(attachmentUid: string) {
  return `eml-${attachmentUid}`;
}

export function parseEmlMessage(src: any): EmlMessage {
  return {
    visualStatus: MessageVisualStatusesEnum.EML_FILE,
    id: 0,
    uid: `eml-${hashCode(JSON.stringify(src))}`,
    url: '',
    conversationId: 0,
    conversationRef: {
      isLastMessage: false,
      isLastManualMessage: false,
      conversationId: 0,
    },
    conversationUrl: '',
    type: MessageTypesEnum.EMAIL,
    visualType: MessageVisualTypesEnum.EMAIL,
    status: MessageStatusesEnum.IMPORTED,
    blurb: '',
    isSeen: false,
    isSenderUnverified: false,
    isReply: false,
    isInbound: false,
    hasMissingBody: false,
    recipients: src.recipients && src.recipients.map(parseRecipient),
    attachments: src.attachments && src.attachments.map(parseEmlAttachment),
    calendarEventUids: [],
    sentAt: src.date,
    createdAt: 0,
    subject: src.subject,

    messageUrl: '',
    body: src.body,
    text: '',
    updatedAt: 0,
    hasQuote: false,
  };
}

// Quick & Dirty, Java-inspired string hashing function.
function hashCode(src: string) {
  let hash = 0;

  if (src.length === 0) {
    return hash;
  }

  for (let i = 0; i < src.length; i++) {
    const char = src.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash;
  }

  return hash;
}

/*
 * API type.
 */

export const ApiMessage = makeApiType('message', RMessage, parseMessage);
export const ApiEmlMessage = makeApiType('emlMessage', RMessage, parseEmlMessage);
